import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import ProfileModal from "../modals/profile-modals/ProfileModal";
import type { User, ProfileData } from '../../types/knowledge-base.ts';
import { AUTH0_CONFIG } from "../../config/api.config.ts";

interface MainLayoutProps {
    user: User;
    children: React.ReactNode;
    modalProps: {
        profileData: ProfileData;
        error: string | null;
        success: string | null;
        isLoading: boolean;
        showProfileModal: boolean;
        handleProfileUpdate: (e: React.FormEvent) => Promise<void>;
        handleDataChange: (data: Partial<ProfileData>) => void;
        setShowProfileModal: (show: boolean) => void;
    };
}

export const MainLayout: React.FC<MainLayoutProps> = ({
                                                          user: initialUser,
                                                          children,
                                                          modalProps
                                                      }) => {
    const location = useLocation();
    const { logout } = useAuth0();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [user] = useState(initialUser);

    const isNonAdmin = !user.role?.includes('admin');
    const isRestrictedAccess = isNonAdmin && location.pathname !== '/rag-applications';

    const handleLogout = async () => {
        try {
            localStorage.clear();
            sessionStorage.clear();
            await logout({
                logoutParams: {
                    returnTo: window.location.origin + '/login'
                }
            });
        } catch (err) {
            console.error('Logout error:', err);
            window.location.href = '/login';
        }
    };

    const handlePasswordReset = () => {
        window.location.href = `https://${AUTH0_CONFIG.domain}/u/reset-password?client_id=${AUTH0_CONFIG.clientId}`;
    };

    if (isRestrictedAccess) {
        return <Navigate to="/rag-applications" replace />;
    }

    return (
        <div className="app-container">
            <Navbar
                user={user}
                onShowProfile={() => modalProps.setShowProfileModal(true)}
                onPasswordReset={handlePasswordReset}
                onLogout={handleLogout}
            />
            <div className="content-wrapper">
                {!isNonAdmin && (
                    <div className={`sidebar-container ${isSidebarCollapsed ? 'collapsed' : 'expanded'}`}>
                        <Sidebar
                            isCollapsed={isSidebarCollapsed}
                            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            userRole={user.role ?? 'user'}
                        />
                    </div>
                )}
                <main className={`main-content ${
                    isNonAdmin
                        ? 'no-sidebar'
                        : isSidebarCollapsed
                            ? 'sidebar-collapsed'
                            : ''
                }`}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={location.pathname}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {children}
                        </motion.div>
                    </AnimatePresence>

                    <ProfileModal
                        show={modalProps.showProfileModal}
                        onHide={() => modalProps.setShowProfileModal(false)}
                        profileData={modalProps.profileData}
                        onUpdate={modalProps.handleProfileUpdate}
                        onChange={modalProps.handleDataChange}
                        error={modalProps.error}
                        success={modalProps.success}
                        isLoading={modalProps.isLoading}
                    />
                </main>
            </div>
        </div>
    );
};

export default MainLayout;